import React, { useState, useEffect, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  Container,
  Grid,
  Paper,
  IconButton,
  Fade,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  styled,
  useScrollTrigger,
  ClickAwayListener,
  Popper,
  Grow,
  MenuList
} from '@mui/material';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import InstationLogo from '../Instation-complete.svg';

// Material UI Icons
import {
  BarChart,
  CalendarMonth,
  KeyboardArrowDown,
  Description,
  Image,
  Dashboard,
  Edit,
  TrendingUp,
  LightbulbOutlined,
  ViewAgenda,
  FormatListBulleted,
  LocalLibrary,
  VideoLibrary,
  LocalFireDepartment,
  People,
  Newspaper,
  Chat,
  CardGiftcard,
  Tag,
  ChevronRight
} from '@mui/icons-material';

// Custom styled components
const NavButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'black',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: 'black'
  }
}));

const FeatureIcon = styled(Avatar)(({ theme, bgcolor }) => ({
  backgroundColor: bgcolor || theme.palette.grey[100],
  width: 36,
  height: 36,
  color: bgcolor ? 'white' : theme.palette.primary.main
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1.5),
  alignItems: 'flex-start',
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: theme.palette.grey[50]
  }
}));

const NavAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  boxShadow: scrolled ? theme.shadows[1] : 'none',
  backgroundColor: 'white',
  backdropFilter: scrolled ? 'blur(10px)' : 'none',
  transition: 'all 0.3s',
  borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : 'none',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%'
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 70, // Increased height
  padding: theme.spacing(0, 2),
  '@media (min-width: 600px)': {
    minHeight: 70, // Maintain same height for consistency
  },
}));

const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1),
  textTransform: 'uppercase'
}));

export default function Navbar() {
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const featuresRef = useRef(null);
  const toolsRef = useRef(null);
  const featuresPopperRef = useRef(null);
  const toolsPopperRef = useRef(null);
  
  // Detect scroll for navbar styling
  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  const handleFeaturesMouseEnter = () => {
    setFeaturesOpen(true);
  };

  const handleFeaturesMouseLeave = () => {
    setFeaturesOpen(false);
  };

  const handleToolsMouseEnter = () => {
    setToolsOpen(true);
  };

  const handleToolsMouseLeave = () => {
    setToolsOpen(false);
  };

  const handleFeaturesToggle = () => {
    setFeaturesOpen((prevOpen) => !prevOpen);
  };

  const handleToolsToggle = () => {
    setToolsOpen((prevOpen) => !prevOpen);
  };

  const handleClickAway = (event) => {
    if (
      featuresRef.current && 
      !featuresRef.current.contains(event.target) &&
      featuresPopperRef.current && 
      !featuresPopperRef.current.contains(event.target)
    ) {
      setFeaturesOpen(false);
    }
    
    if (
      toolsRef.current && 
      !toolsRef.current.contains(event.target) &&
      toolsPopperRef.current && 
      !toolsPopperRef.current.contains(event.target)
    ) {
      setToolsOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderToolMenuItem = (icon, title, subtitle, link, bgColor, iconColor) => (
    <MenuItem component="a" href={link} sx={{ p: 1, borderRadius: 1, mb: 0.5 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: 1.5
      }}>
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: bgColor || 'grey.100',
            color: iconColor || 'primary.main'
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500} color="text.primary">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  );

  const renderFeatureSection = (title, items) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ 
        fontSize: '0.7rem', 
        fontWeight: 600, 
        letterSpacing: '0.5px',
        mb: 2,
        textTransform: 'uppercase'
      }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {items.map((item, index) => (
          <Box 
            key={index}
            component="a" 
            href={item.link}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              p: 1.5,
              borderRadius: 1,
              textDecoration: 'none',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40, 
                mr: 2,
                bgcolor: item.bgColor
              }}
            >
              {item.icon}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={500} color="text.primary">
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.subtitle}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <NavAppBar position="fixed" color="default" scrolled={scrollTrigger}>
          <Container maxWidth="lg">
            <StyledToolbar disableGutters>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: -1 }}>
                <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
                  <img src={InstationLogo} alt="Instation" style={{ height: 38, width: 'auto' }} />
                </a>
                
                {/* Navigation Menu */}
                <Box sx={{ display: 'flex', ml: 5 }}>
                  {/* Features Menu Trigger */}
                  <Box 
                    ref={featuresRef}
                    onMouseEnter={handleFeaturesMouseEnter}
                    onMouseLeave={handleFeaturesMouseLeave}
                  >
                    <NavButton
                      aria-haspopup="true"
                      onClick={handleFeaturesToggle}
                      endIcon={<KeyboardArrowDown />}
                    >
                  Features
                    </NavButton>
                    
                    <Popper
                      open={featuresOpen}
                      anchorEl={featuresRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                      style={{ zIndex: 1300 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          style={{ transformOrigin: 'left top' }}
                        >
                          <Paper 
                            ref={featuresPopperRef}
                            onMouseEnter={handleFeaturesMouseEnter}
                            onMouseLeave={handleFeaturesMouseLeave}
                            sx={{ 
                              width: 1000, 
                              maxWidth: '90vw',
                              borderRadius: 2,
                              mt: 1,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            <Box sx={{ p: 3 }}>
                              <Grid container spacing={3}>
                                {/* COLUMN 1: CONTENT CREATION */}
                                <Grid item xs={12} md={4}>
                                  {renderFeatureSection('CONTENT CREATION', [
                                    { 
                                      title: 'Generate Post', 
                                      subtitle: 'Create AI-powered LinkedIn posts', 
                                      link: 'https://instation.app/features/linkedin-post-generator', 
                                      bgColor: '#4d94ff', 
                                      icon: <Description fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Generate Image', 
                                      subtitle: 'Create visuals for your posts', 
                                      link: 'https://instation.app/features/image-generator', 
                                      bgColor: '#4d94ff', 
                                      icon: <Image fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Carousel Generator', 
                                      subtitle: 'Design LinkedIn slide decks', 
                                      link: 'https://instation.app/features/linkedin-carousel-maker', 
                                      bgColor: '#4d94ff', 
                                      icon: <Dashboard fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Topic Generator', 
                                      subtitle: 'Get content ideas instantly', 
                                      link: 'https://instation.app/features/topic-generator', 
                                      bgColor: '#4d94ff', 
                                      icon: <LightbulbOutlined fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Banner Designer', 
                                      subtitle: 'Create LinkedIn profile banners', 
                                      link: 'https://instation.app/features/banner-designer', 
                                      bgColor: '#4d94ff', 
                                      icon: <ViewAgenda fontSize="small" /> 
                                    },
                                    { 
                                      title: 'GIF Creator', 
                                      subtitle: 'Create animated GIFs', 
                                      link: 'https://instation.app/features/gif-creator', 
                                      bgColor: '#4d94ff', 
                                      icon: <CardGiftcard fontSize="small" /> 
                                    }
                                  ])}
                                </Grid>

                                {/* COLUMN 2: CONTENT MANAGEMENT & PROFILE OPTIMIZATION */}
                                <Grid item xs={12} md={4}>
                                  {renderFeatureSection('CONTENT MANAGEMENT', [
                                    { 
                                      title: 'Posts', 
                                      subtitle: 'Manage your content library', 
                                      link: 'https://instation.app/features/posts', 
                                      bgColor: '#ffb74d', 
                                      icon: <FormatListBulleted fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Calendar', 
                                      subtitle: 'Plan your content schedule', 
                                      link: 'https://instation.app/features/content-calendar', 
                                      bgColor: '#ffb74d', 
                                      icon: <CalendarMonth fontSize="small" /> 
                                    }
                                  ])}
                                  
                                  {renderFeatureSection('LINKEDIN PROFILE OPTIMIZATION', [
                                    { 
                                      title: 'Banner & Profile Section', 
                                      subtitle: 'Optimize your profile', 
                                      link: 'https://instation.app/features/profile-optimization', 
                                      bgColor: '#ba68c8', 
                                      icon: <Edit fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Headline Generator', 
                                      subtitle: 'Create standout headlines', 
                                      link: 'https://instation.app/features/headline-generator', 
                                      bgColor: '#ba68c8', 
                                      icon: <Description fontSize="small" /> 
                                    }
                                  ])}
                                  
                                  {renderFeatureSection('ANALYTICS', [
                                    { 
                                      title: 'LinkedIn Metrics', 
                                      subtitle: 'Track performance data', 
                                      link: 'https://instation.app/features/linkedin-metrics', 
                                      bgColor: '#81c784', 
                                      icon: <BarChart fontSize="small" /> 
                                    }
                                  ])}
                                </Grid>

                                {/* COLUMN 3: CONTENT INSPIRATION & ENGAGEMENT TOOLS */}
                                <Grid item xs={12} md={4}>
                                  {renderFeatureSection('CONTENT INSPIRATION', [
                                    { 
                                      title: 'Viral LinkedIn Posts', 
                                      subtitle: 'Learn from top content', 
                                      link: 'https://instation.app/features/viral-posts', 
                                      bgColor: '#f06292', 
                                      icon: <LocalFireDepartment fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Trending Influencers', 
                                      subtitle: 'Follow top creators', 
                                      link: 'https://instation.app/features/trending-influencers', 
                                      bgColor: '#f06292', 
                                      icon: <People fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Trends & News Finder', 
                                      subtitle: 'Discover trending topics', 
                                      link: 'https://instation.app/features/trends-finder', 
                                      bgColor: '#f06292', 
                                      icon: <Newspaper fontSize="small" /> 
                                    }
                                  ])}
                                  
                                  {renderFeatureSection('ENGAGEMENT TOOLS', [
                                    { 
                                      title: 'Engagement Profiles', 
                                      subtitle: 'Boost your network reach', 
                                      link: 'https://instation.app/features/engagement-profiles', 
                                      bgColor: '#4db6ac', 
                                      icon: <People fontSize="small" /> 
                                    },
                                    { 
                                      title: 'Comment Assistant', 
                                      subtitle: 'Reply to comments faster', 
                                      link: 'https://instation.app', 
                                      bgColor: '#4db6ac', 
                                      icon: <Chat fontSize="small" /> 
                                    }
                                  ])}
                                </Grid>
                              </Grid>
                            </Box>
                            
                            <Box sx={{ 
                              borderTop: '1px solid rgba(0, 0, 0, 0.08)', 
                              p: 2, 
                              bgcolor: 'rgba(0, 0, 0, 0.02)',
                              display: 'flex'
                            }}>
                              <Button 
                                component="a" 
                                href="https://instation.app/features"
                                endIcon={<ChevronRight sx={{ fontSize: 18 }} />}
                                sx={{ 
                                  color: 'primary.main',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    bgcolor: 'transparent',
                                    textDecoration: 'none'
                                  }
                                }}
                              >
                                View all features
                              </Button>
                            </Box>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                  
                  {/* Free Tools Menu Trigger */}
                  <Box
                    ref={toolsRef}
                    onMouseEnter={handleToolsMouseEnter}
                    onMouseLeave={handleToolsMouseLeave}
                  >
                    <NavButton
                      aria-haspopup="true"
                      onClick={handleToolsToggle}
                      endIcon={<KeyboardArrowDown />}
                    >
                      Free Tools
                    </NavButton>
                    
                    <Popper
                      open={toolsOpen}
                      anchorEl={toolsRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                      style={{ zIndex: 1300 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          style={{ transformOrigin: 'left top' }}
                        >
                          <Paper 
                            ref={toolsPopperRef}
                            onMouseEnter={handleToolsMouseEnter}
                            onMouseLeave={handleToolsMouseLeave}
                            sx={{
                              mt: 1.5,
                              width: 500,
                              borderRadius: 2,
                              boxShadow: 3,
                              position: 'relative',
                              '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: 32,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                              },
                            }}
                          >
                            <Box sx={{ width: 500, maxWidth: '100vw', p: 2 }}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ 
                                  fontSize: '1rem', 
                                  fontWeight: 500, 
                                  mb: 1
                                }}>
                                  Free LinkedIn Tools
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  Boost your LinkedIn presence with these free tools
                                </Typography>
                              </Box>
                              
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<Description fontSize="small" />, "Headline Generator", "Create standout headlines", "https://instation.app/free-tools/headline-generator", "#5c6bc0", "#ffffff")}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<Edit fontSize="small" />, "Bio Writer", "Craft compelling bios", "https://instation.app/free-tools/bio-writer", "#9575cd", "#ffffff")}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<VideoLibrary fontSize="small" />, "Video Downloader", "Save LinkedIn videos", "https://instation.app/free-tools/video-downloader", "#e57373", "#ffffff")}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<Tag fontSize="small" />, "Hashtag Finder", "Find trending hashtags", "https://instation.app/free-tools/hashtag-finder", "#66bb6a", "#ffffff")}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<Dashboard fontSize="small" />, "Post Templates", "Ready-to-use templates", "https://instation.app/free-tools/post-templates", "#f06292", "#ffffff")}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderToolMenuItem(<Image fontSize="small" />, "Image Dimensions", "LinkedIn image sizes", "https://instation.app/free-tools/image-dimensions", "#4dd0e1", "#ffffff")}
                                </Grid>
                              </Grid>
                              
                              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2, pt: 2 }}>
                                <Button 
                                  component="a" 
                                  href="https://instation.app/free-tools"
                                  endIcon={<ChevronRight sx={{ fontSize: 18 }} />}
                                  sx={{ 
                                    color: 'primary.main',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    p: 0,
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                      bgcolor: 'transparent',
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  View all free tools
                                </Button>
                              </Box>
                            </Box>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                  
                  {/* Regular Nav Items */}
                  <NavButton href="https://instation.app/pricing">Pricing</NavButton>
                  <NavButton href="https://instation.app/faq">FAQ</NavButton>
                </Box>
              </Box>
              
              {/* Right Menu - Auth Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NavButton 
                  href="https://portal.instation.app/login" 
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' },
                    color: 'black',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      color: 'black'
                    }
                  }}
                >
              Login
                </NavButton>
                <Button 
                  href="https://portal.instation.app/signup"
                  variant="contained" 
                  sx={{ 
                    ml: 2, 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: '#458EFF',
                    '&:hover': {
                      bgcolor: '#3672FF'
                    }
                  }}
                >
                  Sign Up
            </Button>
              </Box>
            </StyledToolbar>
          </Container>
        </NavAppBar>
      </Box>
    </ClickAwayListener>
  );
} 